import {
    PERMISSION_LEVEL_PROPERTY_ID,
    clampPermissionLevel
} from "containers/admin/reportsV2ConfigUtils";

/**
 * Shared logic for the Reports V2 migration notification (REV-17023).
 *
 * Two surfaces reuse this module:
 *   - ReportsMigrationModal  (first-login, once per user/org)
 *   - ReportsMigrationBanner (persistent, dismissible per session)
 *
 * Targeting derives from existing signals: the org still has legacy
 * MicroStrategy reporting access (reporting not disabled), it was not
 * provisioned as a Reports V2 native tenant, Reports V2 is enabled, and the
 * user meets the same permission-level gate used by the Reports V2 menu item
 * so the "Explore Reports V2" CTA is meaningful. Tenants that signed up once
 * Reports V2 became the default never had legacy reporting to migrate off,
 * so they never qualify.
 */

// Sunset date shared by both notification surfaces so their copy stays in sync.
export const MIGRATION_TIMELINE = {
    legacyRemovalDate: "October 1"
};

const REPORTS_V2_PATH = (orgId) => `/${orgId}/reports-v2`;

export function reportsV2Path(orgId) {
    return REPORTS_V2_PATH(orgId);
}

function findConfig(orgConfigs, id) {
    return Array.isArray(orgConfigs)
        ? orgConfigs.find((cfg) => cfg && cfg.id === id)
        : undefined;
}

export function isReportsV2Enabled(orgConfigs) {
    const cfg = findConfig(orgConfigs, "properties/reports-v2-enabled");
    return Boolean(cfg && cfg.value);
}

/**
 * Set at tenant creation (see defaultOrgConfig / OrgService.setOrgConfig) and
 * absent on tenants provisioned before Reports V2 became the default, so an
 * unset value means "legacy reporting tenant".
 */
export function isReportsV2NativeTenant(orgConfigs) {
    const cfg = findConfig(orgConfigs, "properties/reports-v2-native-tenant");
    return Boolean(cfg && cfg.value);
}

export function getReportsV2RequiredLevel(orgConfigs) {
    const cfg = findConfig(orgConfigs, PERMISSION_LEVEL_PROPERTY_ID);
    return clampPermissionLevel(cfg && cfg.value);
}

export function hasReportsV2Permission(currentUser, orgConfigs) {
    return Boolean(
        currentUser &&
            currentUser.superAdminLevel >= getReportsV2RequiredLevel(orgConfigs)
    );
}

/**
 * Whether the current org/user should be treated as part of the legacy
 * reporting (MSTR) migration cohort.
 */
export function isLegacyReportingMigrationCohort(
    currentOrganization,
    orgConfigs,
    currentUser
) {
    if (!currentOrganization) return false;
    if (currentOrganization.disableReporting) return false;
    if (isReportsV2NativeTenant(orgConfigs)) return false;
    return (
        isReportsV2Enabled(orgConfigs) &&
        hasReportsV2Permission(currentUser, orgConfigs)
    );
}

// ---- Persistence helpers -------------------------------------------------
// The authoritative "seen" flag lives in the backend user preferences and is
// surfaced on the redux user via GET /private/user. localStorage is only a
// fast-path cache so we can avoid rendering a flash of the modal on reload.

function seenStorageKey(orgId, userId) {
    return `rev:reportsV2MigrationSeen:${orgId || "default"}:${
        userId || "anon"
    }`;
}

export function isModalSeenLocally(orgId, userId) {
    try {
        return localStorage.getItem(seenStorageKey(orgId, userId)) === "1";
    } catch (e) {
        return false;
    }
}

export function markModalSeenLocally(orgId, userId) {
    try {
        localStorage.setItem(seenStorageKey(orgId, userId), "1");
    } catch (e) {
        // Ignore storage failures (private mode / quota).
    }
}

/** Reads the backend-persisted, per-org flag off the redux user. */
export function isModalSeenForUser(currentUser, orgId) {
    return Boolean(
        currentUser &&
            currentUser.preferences &&
            currentUser.preferences[orgId] &&
            currentUser.preferences[orgId].reportsV2MigrationModalSeen
    );
}

function bannerStorageKey(orgId) {
    return `rev:reportsV2MigrationBannerDismissed:${orgId || "default"}`;
}

export function isBannerDismissedThisSession(orgId) {
    try {
        return sessionStorage.getItem(bannerStorageKey(orgId)) === "1";
    } catch (e) {
        return false;
    }
}

export function dismissBannerForSession(orgId) {
    try {
        sessionStorage.setItem(bannerStorageKey(orgId), "1");
    } catch (e) {
        // Ignore storage failures.
    }
}
